import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Menu, X } from 'lucide-react';
import { toast } from 'sonner';
import { mockWaitlistSignup } from '../utils/mock';
import { Link } from 'react-router-dom';

const WorkWithMe = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });

  const handleWaitlistSignup = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    mockWaitlistSignup(formData);
    toast.success('Welcome to the waitlist! We\'ll be in touch soon.');
    setFormData({ name: '', email: '' });
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-soft-light backdrop-blur-sm border-b border-taupe/20">
        <div className="container mx-auto px-4 sm:px-6 py-2 flex justify-between items-center">
          <Link to="/">
            <div className="logo-container">
              <img
                src="https://customer-assets.emergentagent.com/job_midlife-mentor/artifacts/h49dm4c7_The%20Radiant%20Phoenix%20Logo-2.png"
                alt="The Radiant Phoenix"
                className="h-20 sm:h-24 md:h-32 w-auto" />
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/#about" className="nav-link">About</Link>
            <Link to="/work-with-me" className="nav-link">Work With Me</Link>
            <Link to="/get-in-touch" className="nav-link">Get in Touch</Link>
            <a href="https://lindairuohaofficial.substack.com/subscribe" target="_blank" rel="noopener noreferrer" className="nav-link">Subscribe</a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-900 hover:text-gold transition-colors"
            aria-label="Toggle menu">
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen &&
        <nav className="md:hidden bg-soft-light border-t border-taupe/20">
            <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
              <Link to="/" className="nav-link text-lg py-2" onClick={() => setMobileMenuOpen(false)}>
                Home
              </Link>
              <Link to="/#about" className="nav-link text-lg py-2" onClick={() => setMobileMenuOpen(false)}>
                About
              </Link>
              <Link to="/work-with-me" className="nav-link text-lg py-2" onClick={() => setMobileMenuOpen(false)}>
                Work With Me
              </Link>
              <Link to="/get-in-touch" className="nav-link text-lg py-2" onClick={() => setMobileMenuOpen(false)}>
                Get in Touch
              </Link>
              <a href="https://lindairuohaofficial.substack.com/subscribe" target="_blank" rel="noopener noreferrer" className="nav-link text-lg py-2" onClick={() => setMobileMenuOpen(false)}>
                Subscribe
              </a>
            </div>
          </nav>
        }
      </header>

      {/* Hero Section */}
      <section className="pt-28 sm:pt-36 md:pt-52 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-4xl text-center space-y-4 sm:space-y-6">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light font-display leading-tight text-gray-900 italic">
            Work With Me
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-display-italic text-taupe-dark">
            Transform your midlife journey through deep spiritual reconnection
          </p>
        </div>
      </section>

      {/* Course Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-soft-light">
        <div className="container mx-auto max-w-6xl">
          <Card className="overflow-hidden shadow-xl">
            <div className="grid lg:grid-cols-2 gap-0 lg:gap-8">
              {/* Course Image */}
              <div className="overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_midlife-mentor/artifacts/ua14lldm_brandagency-render-36.png"
                  alt="Rekindle My Radiance - Woman on bicycle with basket of wildflowers"
                  className="w-full h-full object-cover min-h-[250px] sm:min-h-[300px]" />

              </div>

              {/* Course Details */}
              <CardContent className="p-6 sm:p-8 md:p-10 lg:p-12 space-y-4 sm:space-y-6">
                <div className="space-y-3 sm:space-y-4">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light font-display text-gray-900 italic">
                    Rekindle My Radiance
                  </h2>
                  <p className="text-base sm:text-lg md:text-xl font-body text-gray-700 leading-relaxed">
                    A comprehensive 12-week journey designed for women in midlife who are ready to reconnect with their spirit and reclaim their energy, clarity, and confidence.
                  </p>
                </div>

                <div className="space-y-2 sm:space-y-3 text-sm sm:text-base md:text-lg font-body text-gray-700">
                  <h3 className="font-semibold text-lg sm:text-xl text-gray-900">What You'll Experience:</h3>
                  <ul className="space-y-1.5 sm:space-y-2 pl-4 sm:pl-6">
                    <li className="flex items-start">
                      <span className="text-gold mr-2">•</span>
                      <span>Deep spiritual reconnection and inner guidance practices</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-2">•</span>
                      <span>Physical, mental, and emotional healing strategies</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-2">•</span>
                      <span>Weekly live sessions and personalised support</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-2">•</span>
                      <span>Tools to reclaim your energy and emotional balance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-2">•</span>
                      <span>Community of women on the same transformative journey</span>
                    </li>
                  </ul>
                </div>

                <div className="pt-3 sm:pt-4">
                  <div className="bg-soft-light p-4 sm:p-6 rounded-lg">
                    <p className="text-center text-base sm:text-lg font-body text-gray-700 mb-3 sm:mb-4">
                      <span className="font-semibold">Program launching soon!</span><br />
                      Join the waitlist to be the first to know when enrollment opens.
                    </p>
                  </div>
                </div>
              </CardContent>
            </div>
          </Card>
        </div>
      </section>

      {/* Waitlist Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-2xl">
          <Card className="bg-white shadow-xl border-taupe/20">
            <CardContent className="p-6 sm:p-8 md:p-10 lg:p-12 space-y-4 sm:space-y-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light font-display text-gray-900 text-center italic">
                Join The Waitlist
              </h2>
              <p className="text-base sm:text-lg font-body text-gray-700 text-center">
                Be the first to know when Rekindle My Radiance opens for enrollment. Limited spots available.
              </p>
              
              <form onSubmit={handleWaitlistSignup} className="space-y-3 sm:space-y-4 pt-3 sm:pt-4">
                <div>
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full text-base" />
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder="Your email address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full text-base" />
                </div>
                <Button
                  type="submit"
                  className="w-full border-4 border-[#C9A84C] bg-transparent text-[#0D1B2A] hover:bg-[#C9A84C] hover:text-white py-4 sm:py-6 text-base sm:text-lg font-semibold transition-all">
                  Join Waitlist
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-10 md:py-12 px-4 sm:px-6 bg-soft-light border-t border-taupe/20">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col items-center gap-3 sm:gap-4">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_midlife-mentor/artifacts/h49dm4c7_The%20Radiant%20Phoenix%20Logo-2.png"
                alt="The Radiant Phoenix"
                className="h-24 sm:h-32 md:h-40 w-auto" />
            </div>
            <div className="text-center">
              <p className="text-sm sm:text-base font-body text-gray-600">© 2026 The Radiant Phoenix. All rights reserved.

              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>);

};

export default WorkWithMe;